<script setup>
  import {computed} from "vue";

  defineOptions({
    name: "ComponentRange",
  });

  defineProps({
    label: {
      type: String,
      default: "",
    },
    modelValue: {
      type: [String, Number],
      default: 0,
    },
    min: {
      type: [String, Number],
      default: 0,
    },
    max: {
      type: [String, Number],
      default: 100,
    },
    step: {
      type: [String, Number],
      default: 1,
    },
    color: {
      type: [String],
      default() {
        return globalThis.getComputedStyle(document.body).getPropertyValue("--color-mcred");
      },
    },
    colorHover: {
      type: [String],
      default() {
        return globalThis.getComputedStyle(document.body).getPropertyValue("--color-mcred-450");
      },
    },
    datalist: {
      type: Array,
      default: () => [],
    },
    showValue: {
      type: Boolean,
      default: false,
    },
  });

  defineEmits(["update:modelValue"]);

  const inputId = computed(() => {
    return `range-${crypto.randomUUID()}`;
  });

  const datalistId = computed(() => {
    return `datalist-${crypto.randomUUID()}`;
  });
</script>

<template>
  <div class="relative z-0 w-full">
    <label
      v-if="label.length > 0"
      class="origin-0 -z-1 mb-1 block cursor-default"
      :for="inputId"
      :data-test="getTestId('range-label')"
    >
      {{ label }}
    </label>

    <div class="relative" range>
      <input
        :id="inputId"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        :value="modelValue"
        autocomplete="off"
        :data-test="getTestId('range-input')"
        @input="$emit('update:modelValue', parseFloat($event.target.value))"
      />

      <div v-if="showValue" model-value :data-test="getTestId('range-model-value')">
        {{ modelValue }}
      </div>
    </div>

    <div
      v-if="datalist.length > 0"
      :id="datalistId"
      class="-mt-1 flex flex-row place-content-between text-sm text-gray-500 select-none"
    >
      <div v-for="(dLabel, index) in datalist" :key="index" class="w-0">
        <div
          class="flex whitespace-nowrap"
          :class="{
            'justify-start': index === 0,
            'justify-center': index > 0 && index < datalist.length - 1,
            'justify-end': index === datalist.length - 1,
          }"
          :data-test="getTestId(`range-helper-text-${index}`)"
        >
          {{ dLabel }}
        </div>
      </div>
    </div>
  </div>
</template>

<!-- TODO replace with Reka slider-->
<style scoped>
  @reference "@css/frontend.css";
  /* Source (modified): https://toughengineer.github.io/demo/slider-styler */

  div[range] {
    --max: v-bind(max);
    --min: v-bind(min);
    --value: v-bind(modelValue);

    --range: calc(var(--max) - var(--min));
    --ratio: calc((var(--value) - var(--min)) / var(--range));
    --sx: calc(0.5 * 2em + var(--ratio) * (100% - 2em));

    --linear-gradient: linear-gradient(v-bind(color), v-bind(color));
    --linear-gradient-hover: linear-gradient(v-bind(colorHover), v-bind(colorHover));

    --color-primary: v-bind(color);
    --color-primary-hover: v-bind(colorHover);
    --color-light: theme("colors.gray.200");

    input[type="range"] {
      @apply h-9 w-full cursor-pointer appearance-none bg-transparent;

      &:focus {
        @apply outline-0;
      }

      &:active {
        @apply cursor-grabbing;
      }

      &::-webkit-slider-thumb {
        @apply h-8 w-8 appearance-none rounded-full border-0 shadow drop-shadow;
        margin-top: calc(max((1em - 1px - 1px) * 0.5, 0px) - 2em * 0.5);
        background: var(--color-primary);
      }

      &::-moz-range-thumb {
        @apply h-8 w-8 appearance-none rounded-full border-0 shadow drop-shadow;
        background: var(--color-primary);
      }

      &::-webkit-slider-runnable-track {
        @apply h-4 rounded border-0 shadow-none;
        background: var(--color-light);
      }

      &::-moz-range-track {
        @apply rounded border-0 shadow-none;
        height: max(calc(1em - 1px - 1px), 0px);
        background: var(--color-light);
      }

      &::-webkit-slider-thumb:hover,
      &::-webkit-slider-thumb:active {
        background: var(--color-primary-hover);
      }

      &::-moz-range-thumb:hover,
      &::-moz-range-thumb:active {
        background: var(--color-primary-hover);
      }

      &:hover::-webkit-slider-runnable-track,
      &:active::-webkit-slider-runnable-track {
        background: var(--color-light);
      }

      &:hover::-moz-range-track,
      &:active::-moz-range-track {
        background: var(--color-light);
      }

      &::-webkit-slider-runnable-track {
        background:
          var(--linear-gradient) 0 / var(--sx) 100% no-repeat,
          var(--color-light);
      }

      &::-moz-range-track {
        background:
          var(--linear-gradient) 0 / var(--sx) 100% no-repeat,
          var(--color-light);
      }

      &:hover::-webkit-slider-runnable-track,
      &:active::-webkit-slider-runnable-track {
        background:
          var(--linear-gradient-hover) 0 / var(--sx) 100% no-repeat,
          var(--color-light);
      }

      &:hover::-moz-range-track,
      &:active::-moz-range-track {
        background:
          var(--linear-gradient-hover) 0 / var(--sx) 100% no-repeat,
          var(--color-light);
      }
    }

    div[model-value] {
      @apply pointer-events-none absolute top-1.5 flex w-0 justify-center font-semibold select-none;

      color: var(--color-light);
      left: var(--sx);
    }
  }
</style>
